import { api, useApi } from '../api/queries'
import type {
  PointSchemeView,
  PresetView,
  RulesetView,
  SimulationResponse,
  StandingsResponse,
} from '../api/types'
import { DataState } from '../components/DataState'
import { RemovalNotice } from '../components/RemovalNotice'
import { ScenarioLink } from '../components/ScenarioLink'
import { dropSentence } from '../components/RuleProse'
import { formatPoints } from '../lib/decimal'
import { toSimulationBody } from '../lib/apiScenario'
import { FIRST_SEASON, LAST_SEASON, isPristine, type Scenario } from '../lib/scenario'
import { useScenario } from '../lib/useScenario'

/**
 * The scenario editor. The URL is the single source of truth, so a link
 * reproduces a configuration exactly. The live top five on the right is there
 * so a configuration screen is not working blind.
 */
export function ConfigPage() {
  const handle = useScenario()
  const { scenario, set, reset, query } = handle
  const update = (patch: Partial<Scenario>, options?: { replace?: boolean }) =>
    set((current) => ({ ...current, ...patch }), options)

  return (
    <div className="config">
      <section className="config__main">
        <header className="page-head">
          <h1>Configuration</h1>
        </header>

        <Seasons scenario={scenario} update={update} />
        <Scoring scenario={scenario} update={update} />
        <DroppedScores scenario={scenario} update={update} />
        <FastestLap scenario={scenario} update={update} />
        <RoundValues scenario={scenario} update={update} />
        <Penalties scenario={scenario} update={update} />
        <Removals scenario={scenario} update={update} />
        <BarChampions scenario={scenario} update={update} />
        <Rounds scenario={scenario} update={update} />
        <Advanced scenario={scenario} update={update} />

        {!isPristine(scenario) ? (
          <p>
            <button type="button" className="link-button" onClick={reset}>
              Reset everything
            </button>
          </p>
        ) : null}
      </section>

      <aside className="config__aside">
        <Preview scenario={scenario} query={query} />
        <ScenarioLink query={query} />
      </aside>
    </div>
  )
}

type Update = (patch: Partial<Scenario>, options?: { replace?: boolean }) => void

function Section({
  title,
  children,
  note,
}: {
  title: string
  children: React.ReactNode
  note?: string
}) {
  return (
    <section className="config__section">
      <h2>{title}</h2>
      {note ? <p className="muted config__note">{note}</p> : null}
      {children}
    </section>
  )
}

/**
 * Three modes rather than a multi-select over seventy-seven seasons, which is
 * an ergonomic trap: nobody wants to ctrl-click 1964 and 1988 out of a list.
 */
function Seasons({ scenario, update }: { scenario: Scenario; update: Update }) {
  const mode =
    scenario.yearFrom === scenario.yearTo
      ? 'one'
      : scenario.yearFrom === FIRST_SEASON && scenario.yearTo === LAST_SEASON
        ? 'all'
        : 'range'

  return (
    <Section title="Seasons">
      <div className="config__modes">
        <label>
          <input
            type="radio"
            name="season-mode"
            checked={mode === 'one'}
            onChange={() => update({ yearFrom: scenario.yearFrom, yearTo: scenario.yearFrom })}
          />
          One season
        </label>
        <label>
          <input
            type="radio"
            name="season-mode"
            checked={mode === 'range'}
            onChange={() =>
              update({
                yearFrom: scenario.yearFrom,
                yearTo: Math.min(scenario.yearFrom + 9, LAST_SEASON),
              })
            }
          />
          A range
        </label>
        <label>
          <input
            type="radio"
            name="season-mode"
            checked={mode === 'all'}
            onChange={() => update({ yearFrom: FIRST_SEASON, yearTo: LAST_SEASON })}
          />
          Every season
        </label>
      </div>

      {mode !== 'all' ? (
        <div className="config__row">
          <label className="field">
            <span>{mode === 'one' ? 'Season' : 'From'}</span>
            <YearSelect
              value={scenario.yearFrom}
              onChange={(year) =>
                update({
                  yearFrom: year,
                  yearTo: mode === 'one' ? year : Math.max(year, scenario.yearTo),
                })
              }
            />
          </label>
          {mode === 'range' ? (
            <label className="field">
              <span>To</span>
              <YearSelect
                value={scenario.yearTo}
                onChange={(year) => update({ yearTo: Math.max(year, scenario.yearFrom) })}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      <fieldset className="field field--radios">
        <legend>Championship</legend>
        {(['drivers', 'constructors'] as const).map((which) => (
          <label key={which}>
            <input
              type="radio"
              name="config-championship"
              checked={scenario.championship === which}
              onChange={() => update({ championship: which })}
            />
            {which === 'drivers' ? 'Drivers' : 'Constructors'}
          </label>
        ))}
      </fieldset>
    </Section>
  )
}

function YearSelect({ value, onChange }: { value: number; onChange: (year: number) => void }) {
  const years: number[] = []
  for (let year = LAST_SEASON; year >= FIRST_SEASON; year -= 1) years.push(year)
  return (
    <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
      {years.map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  )
}

/**
 * Presets on one side, points tables on the other, each table rendered inline
 * so 25-18-15-12 can be compared with 9-6-4-3 by looking at them.
 */
function Scoring({ scenario, update }: { scenario: Scenario; update: Update }) {
  const presets = useApi<PresetView[]>(['presets'], () =>
    api.GET('/api/v1/points-systems/presets', {}),
  )
  const schemes = useApi<PointSchemeView[]>(['points-systems'], () =>
    api.GET('/api/v1/points-systems', {}),
  )

  return (
    <Section
      title="Scoring"
      note="A preset replaces the whole rule set — the points table, how many results counted, who appears in the table and how ties were broken. A points table replaces only the points."
    >
      <h3 className="config__sub">Presets</h3>
      <div className="config__choices">
        <label className="choice">
          <input
            type="radio"
            name="preset"
            checked={scenario.preset === null}
            onChange={() => update({ preset: null })}
          />
          <span>
            <strong>As it was</strong>
            <span className="muted"> the season’s own rules</span>
          </span>
        </label>
        {(presets.data ?? []).map((preset) => (
          <label key={preset.key} className="choice">
            <input
              type="radio"
              name="preset"
              checked={scenario.preset === preset.key}
              onChange={() => update({ preset: preset.key, schemes: [] })}
            />
            <span>
              <strong>{preset.label}</strong>
              <span className="muted"> {preset.description}</span>
            </span>
          </label>
        ))}
      </div>

      <h3 className="config__sub">Points tables</h3>
      <div className="config__choices">
        <label className="choice">
          <input
            type="radio"
            name="scheme"
            checked={scenario.schemes.length === 0}
            onChange={() => update({ schemes: [] })}
          />
          <span>
            <strong>As it was</strong>
          </span>
        </label>
        {(schemes.data ?? []).map((scheme) => (
          <label key={scheme.key} className="choice">
            <input
              type="radio"
              name="scheme"
              checked={scenario.schemes.includes(scheme.key)}
              onChange={() => update({ schemes: [scheme.key], preset: null })}
            />
            <span>
              <code>{scheme.key}</code>{' '}
              <span className="choice__points">
                {Object.entries(scheme.positions)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .slice(0, 10)
                  .map(([, value]) => formatPoints(value))
                  .join('-')}
              </span>
            </span>
          </label>
        ))}
      </div>
    </Section>
  )
}

function DroppedScores({ scenario, update }: { scenario: Scenario; update: Update }) {
  const resolved = useApi<RulesetView[]>(['rules', scenario.yearFrom], () =>
    api.GET('/api/v1/seasons/{year}/rules', { params: { path: { year: scenario.yearFrom } } }),
  )
  const own = (resolved.data ?? []).find(
    (ruleset) =>
      ruleset.championship ===
      (scenario.championship === 'drivers' ? 'DRIVERS' : 'CONSTRUCTORS'),
  )

  return (
    <Section title="Results that counted">
      <div className="config__modes">
        {[
          ['', 'As it was'],
          ['all', 'Every round counts'],
          ['all-but-one', 'All but the worst'],
          ['best', 'Only the best N'],
        ].map(([value, label]) => (
          <label key={value}>
            <input
              type="radio"
              name="drop"
              checked={current(scenario) === value}
              onChange={() =>
                update({
                  drop:
                    value === ''
                      ? null
                      : value === 'all'
                        ? { mode: 'ALL', n: null }
                        : value === 'all-but-one'
                          ? { mode: 'ALL_BUT_ONE', n: null }
                          : { mode: 'BEST_N', n: scenario.drop?.n ?? 11 },
                })
              }
            />
            {label}
          </label>
        ))}
      </div>
      {scenario.drop?.mode === 'BEST_N' ? (
        <label className="field">
          <span>How many count</span>
          <input
            type="number"
            min={1}
            max={30}
            value={scenario.drop.n ?? 11}
            onChange={(event) =>
              update(
                { drop: { mode: 'BEST_N', n: Math.max(1, Number(event.target.value) || 1) } },
                { replace: true },
              )
            }
          />
        </label>
      ) : null}
      {own ? (
        <p className="config__resolved">
          {scenario.drop === null
            ? dropSentence(own)
            : dropSentence({
                ...own,
                best_rounds_mode: scenario.drop.mode,
                best_rounds_n: scenario.drop.n,
              })}
        </p>
      ) : null}
    </Section>
  )
}

function current(scenario: Scenario): string {
  if (scenario.drop === null) return ''
  if (scenario.drop.mode === 'ALL') return 'all'
  if (scenario.drop.mode === 'ALL_BUT_ONE') return 'all-but-one'
  return 'best'
}

function FastestLap({ scenario, update }: { scenario: Scenario; update: Update }) {
  const rule = scenario.fastestLap
  return (
    <Section
      title="Fastest lap"
      note="Who set the fastest lap is recorded; who would have set it under other circumstances is not. So this changes what the point is worth, never who gets it."
    >
      <div className="config__modes">
        {[
          ['', 'As it was'],
          ['none', 'No point at all'],
          ['any', 'A point, wherever it finished'],
          ['top10', 'A point, if in the top ten'],
        ].map(([value, label]) => (
          <label key={value}>
            <input
              type="radio"
              name="fl"
              checked={flValue(scenario) === value}
              onChange={() =>
                update({
                  fastestLap:
                    value === ''
                      ? null
                      : value === 'none'
                        ? { rule: 'NONE', points: '0', maxPosition: null }
                        : value === 'any'
                          ? { rule: 'ANY', points: rule?.points ?? '1', maxPosition: null }
                          : { rule: 'TOP_N', points: rule?.points ?? '1', maxPosition: 10 },
                })
              }
            />
            {label}
          </label>
        ))}
      </div>
      {rule !== null && rule.rule !== 'NONE' ? (
        <label className="field">
          <span>Points for it</span>
          <input
            type="text"
            inputMode="decimal"
            value={rule.points}
            // Kept as a string the whole way: the 1954 British Grand Prix split
            // one point seven ways, and a float would not survive that.
            onChange={(event) =>
              update(
                { fastestLap: { ...rule, points: event.target.value.replace(/[^\d.]/g, '') } },
                { replace: true },
              )
            }
          />
        </label>
      ) : null}
    </Section>
  )
}

function flValue(scenario: Scenario): string {
  const rule = scenario.fastestLap
  if (rule === null) return ''
  if (rule.rule === 'NONE') return 'none'
  if (rule.rule === 'TOP_N') return 'top10'
  return 'any'
}

/**
 * What a round was worth.
 *
 * Six races in the sport's history were scored at something other than face
 * value, and one of them decided a championship: Monaco 1984 was stopped in
 * the rain and paid half, and Lauda took the title from Prost by half a point.
 */
function RoundValues({ scenario, update }: { scenario: Scenario; update: Update }) {
  const year = scenario.yearFrom
  const rules = useApi<RulesetView[]>(['rules', year], () =>
    api.GET('/api/v1/seasons/{year}/rules', { params: { path: { year } } }),
  )
  const own = (rules.data ?? []).find(
    (ruleset) =>
      ruleset.championship ===
      (scenario.championship === 'drivers' ? 'DRIVERS' : 'CONSTRUCTORS'),
  )
  const theirs = Object.entries(own?.round_multipliers ?? {})
  const mode = scenario.roundValues === null ? '' : 'full'

  return (
    <Section title="What a round was worth">
      {theirs.length > 0 ? (
        <p className="config__resolved">
          {year} scored{' '}
          {theirs
            .map(([round, value]) => `round ${round} at ${formatPoints(value)}×`)
            .join(', ')}
          .
        </p>
      ) : (
        <p className="muted config__note">Every round in {year} counted at face value.</p>
      )}

      <div className="config__modes">
        <label>
          <input
            type="radio"
            name="mult"
            checked={mode === ''}
            onChange={() => update({ roundValues: null })}
          />
          As it was
        </label>
        <label>
          <input
            type="radio"
            name="mult"
            checked={mode === 'full'}
            onChange={() => update({ roundValues: {} })}
          />
          Every round at full points
        </label>
      </div>
      <p className="muted config__note">
        Points have to be recomputed from the scoring table to answer this, because the recorded
        points already carry the season's own multipliers.
      </p>
    </Section>
  )
}

/**
 * Penalties imposed outside the scoring rules.
 *
 * Tyrrell were struck from the 1984 constructors' championship and Schumacher
 * from the 1997 drivers', and both still appear in the published table on
 * their points with no position at all.
 */
function Penalties({ scenario, update }: { scenario: Scenario; update: Update }) {
  const year = scenario.yearFrom
  const rules = useApi<RulesetView[]>(['rules', year], () =>
    api.GET('/api/v1/seasons/{year}/rules', { params: { path: { year } } }),
  )
  const own = (rules.data ?? []).find(
    (ruleset) =>
      ruleset.championship ===
      (scenario.championship === 'drivers' ? 'DRIVERS' : 'CONSTRUCTORS'),
  )
  const penalties = own?.adjustments ?? []

  return (
    <Section title="Penalties">
      {penalties.length > 0 ? (
        <ul className="config__penalties">
          {penalties.map((penalty) => (
            <li key={penalty.entity}>
              <strong>{penalty.entity}</strong>{' '}
              {penalty.action === 'EXCLUDE'
                ? 'struck from the championship'
                : penalty.action === 'SET_POINTS'
                  ? 'set to nothing'
                  : `docked ${formatPoints(penalty.points_delta)} points`}
              {penalty.reason ? <span className="muted"> — {penalty.reason}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted config__note">
          Nothing was penalised in {year}’s {scenario.championship}’ championship.
        </p>
      )}

      <label className="config__toggle">
        <input
          type="checkbox"
          checked={scenario.countPenalised}
          disabled={penalties.length === 0}
          onChange={(event) => update({ countPenalised: event.target.checked })}
        />
        <span>Count the penalised results anyway</span>
      </label>
    </Section>
  )
}

/**
 * Removal, fed by the chosen season's own standings.
 *
 * So you cannot remove somebody who did not race, and each option carries the
 * position and points they officially scored -- which puts "remove the
 * champion" one click away.
 */
function Removals({ scenario, update }: { scenario: Scenario; update: Update }) {
  const year = scenario.yearFrom
  const sweeping = scenario.yearFrom !== scenario.yearTo

  const drivers = useApi<StandingsResponse>(['standings', year, 'drivers'], () =>
    api.GET('/api/v1/seasons/{year}/standings/drivers', { params: { path: { year } } }),
  )
  const constructors = useApi<StandingsResponse>(['standings', year, 'constructors'], () =>
    api.GET('/api/v1/seasons/{year}/standings/constructors', { params: { path: { year } } }),
  )

  const toggle = (field: 'excludeDrivers' | 'excludeConstructors', slug: string) => {
    const existing = scenario[field]
    const next = existing.includes(slug)
      ? existing.filter((item) => item !== slug)
      : [...existing, slug]
    update({ [field]: next } as Partial<Scenario>)
  }

  return (
    <Section title="Remove competitors">
      <RemovalNotice />
      {sweeping ? (
        <p className="muted config__note">
          The lists below come from {year}. A competitor who did not race in one of the other
          seasons is simply left alone there, and the answer says so.
        </p>
      ) : null}

      <div className="config__row config__row--wide">
        <div>
          <h3 className="config__sub">Drivers in {year}</h3>
          <DataState query={drivers} absent={`No drivers’ championship in ${year}.`}>
            {(data) => (
              <ul className="picks">
                {data.items.map((item) => (
                  <li key={item.key}>
                    <label>
                      <input
                        type="checkbox"
                        checked={scenario.excludeDrivers.includes(item.entity)}
                        onChange={() => toggle('excludeDrivers', item.entity)}
                      />
                      <span className="picks__pos">{item.position || '—'}</span>
                      <span className="picks__name">{item.name ?? item.entity}</span>
                      <span className="picks__points">{formatPoints(item.points)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </DataState>
        </div>

        <div>
          <h3 className="config__sub">Constructors in {year}</h3>
          <DataState
            query={constructors}
            absent={`There was no constructors’ championship in ${year}.`}
          >
            {(data) => (
              <ul className="picks">
                {data.items.map((item) => (
                  <li key={item.key}>
                    <label>
                      <input
                        type="checkbox"
                        checked={scenario.excludeConstructors.includes(item.entity)}
                        onChange={() => toggle('excludeConstructors', item.entity)}
                      />
                      <span className="picks__pos">{item.position || '—'}</span>
                      <span className="picks__name">{item.name ?? item.entity}</span>
                      <span className="picks__points">{formatPoints(item.points)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </DataState>
        </div>
      </div>
    </Section>
  )
}

/** Rounds, with the ones the season's own rules already ignore locked. */
/**
 * Formula 2's graduation rule, which is the one option here that reads the
 * other seasons in the range.
 *
 * Its own section rather than a line in `Advanced`: every rule in there is a
 * `<select>` over one season's scoring, and this one is neither. It also has
 * something the others do not -- a precondition -- so it needs room to say
 * that one season cannot answer it.
 */
function BarChampions({ scenario, update }: { scenario: Scenario; update: Update }) {
  const sweeping = scenario.yearFrom !== scenario.yearTo
  return (
    <Section title="Champions barred afterwards">
      <p className="muted config__note">
        In Formula 2 the champion moves up and does not return. Thirty-five drivers won the
        seventy-six championships held, so this does not merely strip the repeats: each
        replacement champion is barred in turn, and the titles cascade.
      </p>
      <label className="config__toggle">
        <input
          type="checkbox"
          checked={scenario.barChampions}
          disabled={!sweeping}
          onChange={(event) => update({ barChampions: event.target.checked })}
        />
        <span>Bar a champion from the seasons after the one he won</span>
      </label>
      {sweeping ? (
        <p className="muted config__note">
          Counted from {scenario.yearFrom}, the first season of this range — so it answers what
          would have happened if the rule had started there, not in 1950.
        </p>
      ) : (
        <p className="muted config__note">
          This needs more than one season: there is no earlier champion to leave out of{' '}
          {scenario.yearFrom}. Widen the range above to use it.
        </p>
      )}
    </Section>
  )
}

function Rounds({ scenario, update }: { scenario: Scenario; update: Update }) {
  const year = scenario.yearFrom
  const rules = useApi<RulesetView[]>(['rules', year], () =>
    api.GET('/api/v1/seasons/{year}/rules', { params: { path: { year } } }),
  )
  const season = useApi<{ total_rounds: number }>(['season', year], () =>
    api.GET('/api/v1/seasons/{year}', { params: { path: { year } } }),
  )

  const own = (rules.data ?? []).find(
    (ruleset) =>
      ruleset.championship ===
      (scenario.championship === 'drivers' ? 'DRIVERS' : 'CONSTRUCTORS'),
  )
  const already = new Set(own?.excluded_rounds ?? [])
  const total = season.data?.total_rounds ?? 0

  if (total === 0) return null

  return (
    <Section title={`Rounds in ${year}`}>
      <ul className="picks picks--rounds">
        {Array.from({ length: total }, (_, index) => index + 1).map((round) => {
          const locked = already.has(round)
          return (
            <li key={round}>
              <label title={locked ? `Already excluded by the ${year} rules` : undefined}>
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={locked || scenario.excludeRounds.includes(round)}
                  onChange={() =>
                    update({
                      excludeRounds: scenario.excludeRounds.includes(round)
                        ? scenario.excludeRounds.filter((item) => item !== round)
                        : [...scenario.excludeRounds, round].sort((a, b) => a - b),
                    })
                  }
                />
                {round}
                {locked ? <span className="muted"> (by the rules)</span> : null}
              </label>
            </li>
          )
        })}
      </ul>
      <p className="muted config__note">
        Rounds you exclude are added to the ones the season already ignored, never substituted
        for them.
      </p>
    </Section>
  )
}

/** The rules the presets change in silence, where they can be seen. */
function Advanced({ scenario, update }: { scenario: Scenario; update: Update }) {
  return (
    <details className="config__section config__advanced">
      <summary>
        <h2>Advanced</h2>
      </summary>
      <p className="muted config__note">
        A preset sets three of these without saying so. They are here so a configuration screen
        does not have to pretend otherwise.
      </p>
      <label className="field">
        <span>Cars per team</span>
        <select
          value={scenario.perSession ?? ''}
          onChange={(event) =>
            update({ perSession: (event.target.value || null) as Scenario['perSession'] })
          }
        >
          <option value="">As it was</option>
          <option value="SUM_POINTS">Every car scores</option>
          <option value="BEST_POINTS">Only the best-placed car scores</option>
        </select>
      </label>
      <label className="field">
        <span>Who appears in the table</span>
        <select
          value={scenario.eligibility ?? ''}
          onChange={(event) =>
            update({ eligibility: (event.target.value || null) as Scenario['eligibility'] })
          }
        >
          <option value="">As it was</option>
          <option value="ALL">Everyone who entered</option>
          <option value="HAS_POINT">Only those who scored</option>
          <option value="HAS_FINISH">Only those classified</option>
          <option value="HAS_FINISH_OR_QUALI">Classified or qualified</option>
        </select>
      </label>
      <label className="field">
        <span>Ties</span>
        <select
          value={scenario.tieBreak ?? ''}
          onChange={(event) =>
            update({
              tieBreak: (event.target.value || null) as Scenario['tieBreak'],
              countback: event.target.value ? scenario.countback : null,
            })
          }
        >
          <option value="">As it was</option>
          <option value="NONE">Left as ties</option>
          <option value="COUNTBACK_ALL">Better positions, every race</option>
          <option value="COUNTBACK_COUNTED">Better positions, counting races only</option>
        </select>
      </label>
      <label className="field">
        <span>Shared cars</span>
        <select
          value={scenario.sharedDrive ?? ''}
          onChange={(event) =>
            update({ sharedDrive: (event.target.value || null) as Scenario['sharedDrive'] })
          }
        >
          <option value="">As it was</option>
          <option value="NONE">No special rule</option>
          <option value="NO_POINTS">No points for a shared car</option>
          <option value="SHARED">Split between the drivers</option>
          <option value="SHARED_MULTI">Split, however many shared it</option>
          <option value="SHARED_HIGHEST_FINISH">To the better-placed drive</option>
        </select>
      </label>
    </details>
  )
}

/** The top five, live, so the configuration screen is not blind. */
function Preview({ scenario, query }: { scenario: Scenario; query: string }) {
  const body = toSimulationBody({ ...scenario, yearTo: scenario.yearFrom }, { delta: false })
  const simulation = useApi<SimulationResponse>(['config-preview', body.years[0], query], () =>
    api.POST('/api/v1/simulations/standings', { body }),
  )

  return (
    <div className="preview">
      <h2>{scenario.yearFrom}, top five</h2>
      <DataState query={simulation} skeleton={<p className="muted">Working…</p>}>
        {(data) => {
          const season = data.results[0]
          if (season === undefined) return <p className="muted">Nothing to show.</p>
          return (
            <>
              <ol className="preview__list">
                {season.standings.slice(0, 5).map((item) => (
                  <li key={item.key}>
                    <span>{item.name ?? item.entity}</span>
                    <span className="num">{formatPoints(item.points)}</span>
                  </li>
                ))}
              </ol>
              {season.champion_changed ? (
                <p className="preview__changed">
                  Champion changes: officially{' '}
                  {season.official_champion_name ?? season.official_champion}.
                </p>
              ) : (
                <p className="muted">Same champion as the record.</p>
              )}
            </>
          )
        }}
      </DataState>
    </div>
  )
}
